import marketingGraphic from "@/assets/graphic-social-media-marketing.jpg";
import growthGraphic from "@/assets/graphic-growth-automation.jpg";
import analyticsScreenshot from "@/assets/analytics-dashboard-screenshot.jpeg";
import campaignsScreenshot from "@/assets/campaigns-automation-preview.jpg";

const steps = [
  {
    color: "from-violet-500 to-fuchsia-600",
    tagColor: "bg-violet-100 text-violet-700",
    bulletColor: "bg-violet-500/10 text-violet-500",
    image: marketingGraphic,
    title: "AI-Powered Marketing, Done Completely For You",
    description: "Stop spending hours writing posts, creating graphics, and figuring out what to say on each platform. ClickSendAI does all of it automatically — every single day — so you can focus on running your business.",
    bullets: [
      { icon: "ri-quill-pen-fill", text: "AI writes unique posts tailored to your brand voice and niche" },
      { icon: "ri-image-fill", text: "Auto-generates images, captions, and hashtags for every platform" },
      { icon: "ri-calendar-check-fill", text: "Publishes daily without you touching a single button" },
      { icon: "ri-cpu-fill", text: "Learns your audience over time to improve engagement automatically" },
      { icon: "ri-time-fill", text: "Saves 10–20 hours per week in content creation and scheduling" },
    ],
    tags: ["AI Content Creation", "Done-For-You", "Daily Auto-Posting", "No Writing Needed"],
  },
  {
    color: "from-blue-500 to-cyan-500",
    tagColor: "bg-blue-100 text-blue-700",
    bulletColor: "bg-blue-500/10 text-blue-500",
    image: growthGraphic,
    imgShrink: true,
    title: "Auto-Post Across 30+ Networks, Groups & Social Sites",
    description: "One post. Every platform. Instantly. Your content gets published to the social networks, Facebook Groups, forums, video sites, image sites, and business directories where your ideal customers are already spending time.",
    bullets: [
      { icon: "ri-facebook-fill", text: "Facebook Pages, Groups, and Business Profiles" },
      { icon: "ri-instagram-fill", text: "Instagram, TikTok, Pinterest, and YouTube Shorts" },
      { icon: "ri-linkedin-fill", text: "LinkedIn, Twitter/X, Reddit, and Quora" },
      { icon: "ri-global-fill", text: "20+ business directories, image sites, and niche forums" },
      { icon: "ri-group-fill", text: "Targets niche communities where your exact audience already hangs out" },
    ],
    tags: ["30+ Platforms", "Facebook Groups", "Video & Image Sites", "Business Directories"],
  },
  {
    color: "from-emerald-500 to-teal-500",
    tagColor: "bg-emerald-100 text-emerald-700",
    bulletColor: "bg-emerald-500/10 text-emerald-500",
    image: campaignsScreenshot,
    title: "Set It Once, Post Forever on Autopilot",
    description: "Configure your posting schedule in minutes. ClickSendAI takes care of every post, every day, at the optimal time for each platform — without you ever having to log in again.",
    bullets: [
      { icon: "ri-clockwise-2-fill", text: "Posts automatically at the best times for each network" },
      { icon: "ri-send-plane-fill", text: "Bulk scheduling sends weeks of content in one setup" },
      { icon: "ri-notification-fill", text: "Alerts you only when something needs your attention" },
      { icon: "ri-refresh-fill", text: "Content recycles and refreshes so it never goes stale" },
    ],
    tags: ["Smart Scheduling", "Optimal Timing", "Bulk Publishing"],
  },
  {
    color: "from-amber-500 to-orange-500",
    tagColor: "bg-amber-100 text-amber-700",
    bulletColor: "bg-amber-500/10 text-amber-500",
    image: analyticsScreenshot,
    title: "Track Every Click, Lead, and New Member",
    description: "See exactly what's working and what's driving growth. Real-time dashboards show you which platforms, posts, and campaigns are bringing in the most traffic and conversions.",
    bullets: [
      { icon: "ri-line-chart-fill", text: "Real-time stats across every connected platform" },
      { icon: "ri-user-add-fill", text: "Tracks new members and leads generated from your posts" },
      { icon: "ri-bar-chart-box-fill", text: "Shows which content types get the most clicks and shares" },
      { icon: "ri-download-fill", text: "Export reports to share with your team or clients" },
    ],
    tags: ["Real-time Data", "ROI Tracking", "Growth Reports"],
  },
];

export default function HowItWorksSection() {
  return (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wider mb-4">
            What We Do For You
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Everything Done For You —{" "}
            <span className="bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
              On Autopilot
            </span>
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            No marketing experience needed. ClickSendAI handles everything from content creation to publishing — you just watch your community grow.
          </p>
        </div>

        <div className="space-y-20">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex flex-col ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} items-stretch gap-12`}
            >
              {/* Text */}
              <div className="flex-1 flex flex-col justify-center">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-500 text-base leading-relaxed mb-6">{step.description}</p>

                <ul className="space-y-3 mb-6">
                  {step.bullets.map((b) => (
                    <li key={b.text} className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${step.bulletColor}`}>
                        <i className={`${b.icon} text-sm`}></i>
                      </div>
                      <span className="text-sm text-gray-600 leading-relaxed">{b.text}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap gap-2">
                  {step.tags.map(tag => (
                    <span key={tag} className={`px-3 py-1 rounded-full text-xs font-medium ${step.tagColor}`}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className="flex-1 flex items-center justify-center">
                <div className={`relative rounded-2xl overflow-hidden ${step.imgShrink ? "w-[85%]" : "w-full"}`}>
                  <div className={`absolute -inset-1 bg-gradient-to-r ${step.color} rounded-2xl blur-lg opacity-30`}></div>
                  {step.image ? (
                    <img
                      src={step.image as string}
                      alt={step.title}
                      className="relative w-full h-auto object-contain rounded-2xl border border-gray-100"
                    />
                  ) : (
                    <div className={`relative w-full rounded-2xl border border-gray-100 bg-gradient-to-br ${step.color} flex items-center justify-center`} style={{ minHeight: 300 }}>
                      <div className="text-center text-white/90 p-8">
                        <i className="ri-calendar-schedule-line text-6xl mb-4 block opacity-80"></i>
                        <p className="text-lg font-semibold">Smart Auto-Scheduling</p>
                        <p className="text-sm opacity-70 mt-2">Posts at optimal times across every platform</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
